import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, Typography, Chip, Divider, Alert,
  IconButton, CircularProgress,
} from '@mui/material';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import FactoryIcon from '@mui/icons-material/Factory';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import ReplayIcon from '@mui/icons-material/Replay';
import ScienceIcon from '@mui/icons-material/Science';
import HistoryIcon from '@mui/icons-material/History';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import * as shipmentMgmtApi from '../../lib/api/shipmentMgmt';

const SOURCE_TYPE_MAP = {
  PRODUCTION: { label: '납품', icon: <FactoryIcon fontSize="small" />, color: 'primary' },
  WAREHOUSE: { label: '창고', icon: <WarehouseIcon fontSize="small" />, color: 'secondary' },
  RETURN: { label: '반품 출고', icon: <ReplayIcon fontSize="small" />, color: 'warning' },
  SAMPLE: { label: '샘플 출고', icon: <ScienceIcon fontSize="small" />, color: 'info' },
};

const STATUS_MAP = {
  PENDING: { label: '대기', color: 'default' },
  APPROVED: { label: '승인', color: 'info' },
  COMPLETED: { label: '완료', color: 'success' },
  CANCELLED: { label: '취소', color: 'error' },
};

const ACTION_MAP = {
  CREATED: { label: '등록', color: 'grey' },
  APPROVED: { label: '승인', color: 'info' },
  STATEMENT_ISSUED: { label: '거래명세표 발급', color: 'primary' },
  STOCK_DEDUCTED: { label: '재고 차감', color: 'warning' },
  COMPLETED: { label: '완료', color: 'success' },
  CANCELLED: { label: '취소', color: 'error' },
};

const getLatestDoc = (docs) => {
  if (!Array.isArray(docs) || docs.length === 0) return null;
  return [...docs].sort((a, b) => String(b.issuedAt || '').localeCompare(String(a.issuedAt || '')))[0];
};

const saveBlob = (blob, fileName, inline = false) => {
  const url = window.URL.createObjectURL(blob);
  if (inline) {
    window.open(url, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    return;
  }

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const filenameFromResponse = (res, fallback) => {
  const disposition = res.headers?.['content-disposition'] || '';
  const fileNameHeader = res.headers?.filename || res.headers?.FileName;
  const match = disposition.match(/filename="?(.*?)"?$/i);
  const rawName = fileNameHeader || match?.[1] || fallback;
  try {
    return decodeURIComponent(rawName);
  } catch {
    return rawName;
  }
};

const InfoRow = ({ label, children }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Box>{children}</Box>
  </Box>
);

const ShipmentMgmtDetailDialog = ({ shipmentId, onClose, onRefresh, canProcessShipment = false }) => {
  const [loading, setLoading] = useState(false);
  const [shipment, setShipment] = useState(null);
  const [history, setHistory] = useState([]);
  const [statementDocs, setStatementDocs] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [statementLoading, setStatementLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!shipmentId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await shipmentMgmtApi.getShipment(shipmentId);
      if (res.data?.success) {
        setShipment(res.data.data.shipment);
        setHistory(res.data.data.history || []);
        setStatementDocs(res.data.data.statementDocs || []);
      }
    } catch (err) {
      setError('출고 정보를 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  }, [shipmentId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStatusChange = async (newStatus) => {
    if (!shipment) return;
    if (!canProcessShipment) return;
    const confirmMsg = {
      APPROVED: '이 출고를 승인하시겠습니까?',
      COMPLETED: '출고 완료 처리하시겠습니까? 거래명세표가 필요한 출고는 완료 전에 자동 발급됩니다.',
      CANCELLED: '이 출고를 취소하시겠습니까?',
    };
    if (!window.confirm(confirmMsg[newStatus] || '상태를 변경하시겠습니까?')) return;

    setActionLoading(true);
    try {
      await shipmentMgmtApi.updateShipmentStatus(shipment.shipmentId, { status: newStatus });
      await fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      alert(err?.response?.data?.message || '상태 변경에 실패했습니다.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleIssueStatement = async () => {
    if (!shipment) return;
    if (!canProcessShipment) return;
    setStatementLoading(true);
    try {
      await shipmentMgmtApi.issueStatement(shipment.shipmentId);
      await fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      alert(err?.response?.data?.message || '거래명세표 발급에 실패했습니다.');
    } finally {
      setStatementLoading(false);
    }
  };

  const handleDownloadStatement = async (format, inline = false) => {
    if (!shipment) return;
    const latestDoc = getLatestDoc(statementDocs);
    if (!latestDoc) {
      alert('발급된 거래명세표가 없습니다.');
      return;
    }

    setStatementLoading(true);
    try {
      const res = await shipmentMgmtApi.downloadStatement(shipment.shipmentId, latestDoc.docId, { format, inline });
      const extension = format === 'pdf' ? 'pdf' : 'xlsx';
      const fileName = filenameFromResponse(res, `${latestDoc.docNo || shipment.shipmentNumber}.${extension}`);
      saveBlob(res.data, fileName, inline);
    } catch (err) {
      alert(err?.response?.data?.message || '거래명세표 다운로드에 실패했습니다.');
    } finally {
      setStatementLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!shipment) return;
    if (!canProcessShipment) return;
    if (!window.confirm('이 출고를 삭제하시겠습니까?')) return;

    setActionLoading(true);
    try {
      await shipmentMgmtApi.deleteShipment(shipment.shipmentId);
      if (onRefresh) onRefresh();
      onClose();
    } catch (err) {
      alert(err?.response?.data?.message || '삭제에 실패했습니다.');
    } finally {
      setActionLoading(false);
    }
  };

  if (!shipmentId) return null;

  const sourceInfo = SOURCE_TYPE_MAP[shipment?.sourceType] || SOURCE_TYPE_MAP.PRODUCTION;
  const statusInfo = STATUS_MAP[shipment?.status] || STATUS_MAP.PENDING;
  const isPending = shipment?.status === 'PENDING';
  const isApproved = shipment?.status === 'APPROVED';
  const canApprove = isPending;
  const canComplete = isPending || isApproved;
  const canCancel = isPending || isApproved;
  const latestStatementDoc = getLatestDoc(statementDocs);

  return (
    <Dialog open={!!shipmentId} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        출고 상세
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : shipment ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <InfoRow label="출고번호">
              <Typography variant="body2" fontWeight="bold">{shipment.shipmentNumber}</Typography>
            </InfoRow>
            <InfoRow label="출고 유형">
              <Chip
                icon={sourceInfo.icon}
                label={sourceInfo.label}
                size="small"
                variant="outlined"
                color={sourceInfo.color}
              />
            </InfoRow>
            <InfoRow label="상태">
              <Chip label={shipment.statusName || statusInfo.label} size="small" color={statusInfo.color} />
            </InfoRow>
            <InfoRow label="출고일">
              <Typography variant="body2">{shipment.shipmentDate}</Typography>
            </InfoRow>
            <InfoRow label="수량">
              <Typography variant="body2" fontWeight="bold">{shipment.quantity?.toLocaleString()}개</Typography>
            </InfoRow>

            {/* 값이 없어도 라벨은 남긴다. 예전에는 통째로 숨겨서 "고객사 항목 자체가 없는"
                것처럼 보였다. 창고 입고 건은 고객사가 없는 게 정상이라 그걸 알려줘야 한다. */}
            <InfoRow label="고객사">
              {shipment.customerNm ? (
                <Typography variant="body2">{shipment.customerNm}</Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  지정 안 됨 (창고 입고 건)
                </Typography>
              )}
            </InfoRow>
            {shipment.planNumber && (
              <InfoRow label="생산계획">
                <Typography variant="body2">{shipment.planNumber}</Typography>
              </InfoRow>
            )}
            {shipment.productName && (
              <InfoRow label="제품명">
                <Typography variant="body2">{shipment.productName}</Typography>
              </InfoRow>
            )}
            {shipment.whNm && (
              <InfoRow label="창고">
                <Typography variant="body2">{shipment.whNm}</Typography>
              </InfoRow>
            )}
            {shipment.stockProductName && (
              <InfoRow label="재고 제품">
                <Typography variant="body2">{shipment.stockProductName}</Typography>
              </InfoRow>
            )}
            {shipment.remarks && (
              <InfoRow label="비고">
                <Typography variant="body2" sx={{ maxWidth: 250, textAlign: 'right' }}>{shipment.remarks}</Typography>
              </InfoRow>
            )}

            <InfoRow label="거래명세표">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Chip
                  label={shipment.statementIssuedYn === 'Y' ? '발급됨' : '미발급'}
                  size="small"
                  color={shipment.statementIssuedYn === 'Y' ? 'success' : 'default'}
                  variant={shipment.statementRequiredYn === 'Y' ? 'filled' : 'outlined'}
                />
                {shipment.statementRequiredYn === 'Y' && (
                  <Chip label="필수" size="small" color="warning" variant="outlined" />
                )}
              </Box>
            </InfoRow>
            {latestStatementDoc && (
              <InfoRow label="최근 명세표">
                <Typography variant="body2" sx={{ maxWidth: 260, textAlign: 'right' }}>
                  {latestStatementDoc.docNo} ({latestStatementDoc.issuedAt})
                </Typography>
              </InfoRow>
            )}

            <InfoRow label="등록자">
              <Typography variant="body2">{shipment.createdByName} ({shipment.createdAt})</Typography>
            </InfoRow>

            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReceiptLongIcon fontSize="small" color="action" />
                <Typography variant="subtitle2">거래명세표</Typography>
                <Chip label={`${statementDocs.length}건`} size="small" />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ReceiptLongIcon />}
                  onClick={handleIssueStatement}
                  disabled={!canProcessShipment || statementLoading || actionLoading}
                >
                  발급
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownloadStatement('xlsx')}
                  disabled={!latestStatementDoc || statementLoading || actionLoading}
                >
                  Excel
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={() => handleDownloadStatement('pdf', true)}
                  disabled={!latestStatementDoc || statementLoading || actionLoading}
                >
                  PDF
                </Button>
              </Box>
            </Box>

            {/* 이력 타임라인 */}
            {history.length > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <HistoryIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2">처리 이력</Typography>
                </Box>
                <Timeline sx={{ p: 0, m: 0, '& .MuiTimelineItem-root:before': { display: 'none' } }}>
                  {history.map((h, idx) => {
                    const actionInfo = ACTION_MAP[h.action] || { label: h.action, color: 'grey' };
                    return (
                      <TimelineItem key={h.historyId || idx}>
                        <TimelineOppositeContent sx={{ flex: 0.3, py: 0.5, px: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {h.createdAt?.substring(5, 16)}
                          </Typography>
                        </TimelineOppositeContent>
                        <TimelineSeparator>
                          <TimelineDot color={actionInfo.color} sx={{ my: 0.5 }} variant="outlined" />
                          {idx < history.length - 1 && <TimelineConnector />}
                        </TimelineSeparator>
                        <TimelineContent sx={{ py: 0.5, px: 1 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {actionInfo.label}
                          </Typography>
                          {h.remarks && (
                            <Typography variant="caption" color="text.secondary">
                              {h.remarks}
                            </Typography>
                          )}
                          {h.createdByName && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {h.createdByName}
                            </Typography>
                          )}
                        </TimelineContent>
                      </TimelineItem>
                    );
                  })}
                </Timeline>
              </>
            )}
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, justifyContent: 'space-between' }}>
        <Box>
          {isPending && (
            <Button
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              disabled={!canProcessShipment || actionLoading}
            >
              삭제
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>닫기</Button>
          {canCancel && (
            <Button
              color="error"
              variant="outlined"
              size="small"
              startIcon={<CancelIcon />}
              onClick={() => handleStatusChange('CANCELLED')}
              disabled={!canProcessShipment || actionLoading}
            >
              취소
            </Button>
          )}
          {canApprove && (
            <Button
              color="info"
              variant="contained"
              size="small"
              startIcon={<ThumbUpIcon />}
              onClick={() => handleStatusChange('APPROVED')}
              disabled={!canProcessShipment || actionLoading}
            >
              승인
            </Button>
          )}
          {canComplete && (
            <Button
              color="success"
              variant="contained"
              size="small"
              startIcon={<CheckCircleIcon />}
              onClick={() => handleStatusChange('COMPLETED')}
              disabled={!canProcessShipment || actionLoading}
            >
              완료
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ShipmentMgmtDetailDialog;
